<template>
  <div ref="target" class="currency-selector flex w-full flex-col items-end">
    <!-- Trigger: JPY ∨ / USD ∨ -->
    <button
      class="flex items-center justify-end gap-1 font-Spectral text-[12px] font-normal leading-4 tracking-wider"
      type="button"
      :aria-controls="dropdownId"
      :aria-expanded="isOpen"
      :class="props.disabled ? 'cursor-default text-light-fg-disabled' : 'cursor-pointer text-light-fg-muted'"
      :disabled="props.disabled"
      @click="!props.disabled && (isOpen = !isOpen)"
      @keydown.arrow-down.prevent="!props.disabled && onTriggerArrowDown()"
      @keydown.enter.prevent="!props.disabled && (isOpen = !isOpen)"
      @keydown.escape="isOpen = false"
      @keydown.space.prevent="!props.disabled && (isOpen = !isOpen)"
    >
      <span>{{ selectedCurrency }}</span>
      <img alt="arrow" class="h-2 w-2 transition-transform duration-200" :class="{ 'rotate-180': isOpen }" :src="arrowIconUrl" />
    </button>

    <transition name="expand">
      <div v-if="isOpen" class="mt-2 w-full overflow-hidden bg-white">
        <ul :id="dropdownId" class="flex flex-col" role="listbox">
          <li
            v-for="(currency, index) in supportedCurrencies"
            :key="currency.code"
            :ref="(el) => setOptionRef(el, index)"
            class="group flex cursor-pointer items-center px-5 py-3 hover:bg-light-bg-inset"
            role="option"
            :aria-selected="selectedCurrency === currency.code"
            :tabindex="selectedCurrency === currency.code ? 0 : -1"
            @click="() => selectCurrency(currency.code)"
            @keydown.arrow-down.prevent="() => focusOption(index + 1)"
            @keydown.arrow-up.prevent="() => focusOption(index - 1)"
            @keydown.enter.prevent="() => selectCurrency(currency.code)"
            @keydown.escape="() => (isOpen = false)"
            @keydown.space.prevent="() => selectCurrency(currency.code)"
          >
            <!-- Currency Icon (24x24, Centered) -->
            <div
              class="mr-2 h-6 w-6 shrink-0 bg-current"
              role="img"
              :aria-label="currency.code"
              :class="selectedCurrency === currency.code ? 'text-light-fg-accent' : 'text-light-fg-default'"
              :style="{
                maskImage: `url(${currency.icon})`,
                WebkitMaskImage: `url(${currency.icon})`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }"
            />
            <span class="flex-1 font-Spectral text-[14px]" :class="selectedCurrency === currency.code ? 'font-medium text-light-fg-accent' : 'font-normal text-light-fg-default'">
              {{ currency.code }} ({{ currency.label }})
            </span>
            <img v-if="selectedCurrency === currency.code" alt="check" class="h-4 w-4" :src="checkIconUrl" />
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { useId } from 'vue';

import arrowIconUrl from '@/assets/img/arrow.svg';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { sendGAWabunkaChangeCurrencyEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/changeCurrency';

// public/ 配下のアイコン参照は :src バインディング経由で行う（コンパイル時 import 化を回避、JCAT-4981）
const checkIconUrl = '/icon/currencies/check.svg';

interface Props {
  gaLocation?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  gaLocation: 'price_section',
  disabled: false,
});

const { selectedCurrency, supportedCurrencies } = useCurrency();
const isOpen = ref(false);
const target = ref<HTMLElement | null>(null);
const dropdownId = useId();
const optionRefs = ref<(HTMLElement | null)[]>([]);

const setOptionRef = (el: unknown, index: number) => {
  optionRefs.value[index] = el as HTMLElement | null;
};

const focusOption = (index: number) => {
  const clamped = Math.max(0, Math.min(index, supportedCurrencies.length - 1));
  optionRefs.value[clamped]?.focus();
};

const onTriggerArrowDown = () => {
  isOpen.value = true;
  nextTick(() => focusOption(0));
};

const selectCurrency = (code: string) => {
  if (selectedCurrency.value === code) {
    isOpen.value = false;
    return;
  }
  selectedCurrency.value = code;
  sendGAWabunkaChangeCurrencyEvent(code, props.gaLocation);
  isOpen.value = false;
};

onClickOutside(target, () => {
  isOpen.value = false;
});
</script>

<style scoped lang="scss">
.expand-enter-active,
.expand-leave-active {
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  max-height: 600px;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
